import { DashboardActionItemFragment } from '@/modules/tasks/features/actionItems/graphql/fragments/generated/ActionItem';
import useActionItemActions from '@/modules/tasks/features/actionItems/hooks/useActionItemActions';
import {
  ActionItemsFilters,
  ActionItemType,
  EntityTagType,
  IconSettings,
  ProviderType,
  RelatedEntityTypeEnum,
  SortType,
  StageConfigSchema,
  StatusType,
  TagUnion,
  UserType,
  UserViewFieldSchema,
} from '@/types/schema';

import { FileMinifiedFragment } from '../../graphql/fragments/generated/fileMinified';
import { DashboardActionsDispatch } from '../../hooks/useDashboardActions';
import { UseTaskDashboardComponentsReturnType } from '../../hooks/useTaskDashboardComponents';
import { GroupedActionItem } from '../../types';
import { SubtaskViewType } from '../../utils/getSubtasksViewItems';

export interface SortAndFilterType {
  sort: SortType[];
  filter: ActionItemsFilters[];
}

export interface GroupedListProps {
  groupedActionItems: GroupedActionItem[];
  setInViewTask: UseTaskDashboardComponentsReturnType['dialog']['setInViewTask'];
  actions: ReturnType<typeof useActionItemActions>['actions'];
  providers?: ProviderType[];
  columnWidth: ColumnWidth;
  setColumnWidth: (width: ColumnWidth) => void;
  sortedTaskViewConfig?: UserViewFieldSchema[] | null;
  participants?: UserType[];
  allTags: TagUnion[];
  entityId?: string;
  dispatch: DashboardActionsDispatch;
  entity?: RelatedEntityTypeEnum;
  fetchMore?: (actionItem: GroupedActionItem) => void;
  defaultAssigneeId?: string;
  fileId?: string;
  groupBy?: string;
  disableAddTask?: boolean;
  fetchingMoreGroupKey?: string;
  fetchMoreGroups?: () => void;
  entityName: string | undefined;
  addTasksViaAI?: boolean;
  subTaskView?: SubtaskViewType;
  files?: FileMinifiedFragment[];
  systemGeneratedFiles?: FileMinifiedFragment[];
  sortAndFilter: SortAndFilterType;
}

export enum TableRowType {
  Data = 'DATA',
  LoadMore = 'LOAD_MORE',
  AddNewTask = 'ADD_NEW_TASK',
  Fetcher = 'ROW_FETCHER',
}

export type GroupedByStatusType = Pick<ActionItemType, 'status' | 'statusId' | 'category'> & {
  statusInfo?: Pick<StatusType, 'id' | 'name' | 'category' | 'icon' | 'progress'> | null;
};

export type GroupedByStageType = Pick<StageConfigSchema, 'stageId' | 'name'> & {
  iconSettings?: Pick<IconSettings, 'type' | 'value'> | null;
};

export type GroupedTableGroupData = {
  groupBy?: string; // FIXME: @aryan02420 add type - (keyof ActionItemType) | `tag:${string}` ...
  groupByKey?: string;
  groupedByUser?: UserType | null;
  groupedByStatus?: GroupedByStatusType | null;
  groupedByStage?: GroupedByStageType | null;
  groupedByTag?: EntityTagType | null;
  totalCount?: number | null;
};

export type TableRowWithData = {
  type: TableRowType.Data;
  data: DashboardActionItemFragment;
  id: string;
  children?: (TableRowFetcher | TableRowWithData)[];
  childCount?: number;
  groupByKey?: string;
};

export type TableRowWithLoadMore = {
  type: TableRowType.LoadMore;
  data: GroupedActionItem;
  id: string;
};

export type TableRowWithAddNewTask = {
  type: TableRowType.AddNewTask;
  data: GroupedActionItem;
  id: string;
};

export type TableRowFetcher = {
  type: TableRowType.Fetcher;
  id: string;
  parentId: string;
};

export type TableRowData =
  | TableRowWithData
  | TableRowWithLoadMore
  | TableRowWithAddNewTask
  | TableRowFetcher;

export type ColumnWidth = Record<string, number>;
